import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import MultiLogFileCellRenderer from 'components/common/multiLogFileCellRenderer'
import { formatDate } from 'utils/helpers'

// Aggregator + Store shown together (Store stacked below Aggregator) so a
// row's log identity reads as one unit, matching how the FtpCreationLog is
// scoped, without taking up two separate columns.
const AggregatorStoreCellRenderer = ({ data }: ICellRendererParams) => (
  <div className="flex flex-col justify-center py-1 leading-tight">
    <span className="truncate" title={data?.aggregatorCode}>
      {data?.aggregatorCode}
    </span>
    <span className="truncate text-gray-500" title={data?.storeCode}>
      {data?.storeCode}
    </span>
  </div>
)

// Creation + Completion dates stacked in one cell instead of two separate
// columns, packed tight (no gap) to match the other multi-line cells.
const DateCellRenderer = ({ data }: ICellRendererParams) => (
  <div className="flex flex-col justify-center py-1 leading-tight">
    <span title="Creation Date">
      {formatDate(data?.createdAt, { includeTime: true })}
    </span>
    <span title="Completion Date">
      {data?.completionDate
        ? formatDate(data.completionDate, { includeTime: true })
        : '-'}
    </span>
  </div>
)

export const getColumns = (page: number) =>
  [
    {
      headerName: 'ID',
      field: 'uuid',
      valueFormatter: (params: ValueFormatterParams) => {
        return (params.node?.rowIndex as number) + 1 + page
      },
      maxWidth: 120,
    },
    {
      headerName: 'Aggregator / Store',
      field: 'aggregatorCode',
      autoHeight: true,
      cellRenderer: AggregatorStoreCellRenderer,
      maxWidth: 180,
    },
    { headerName: 'Status', field: 'status', maxWidth: 150 },
    {
      headerName: 'File Path',
      field: 'filePath',
      autoHeight: true,
      minWidth: 300,
      cellRenderer: (params: ICellRendererParams) => (
        <MultiLogFileCellRenderer params={params} downloadable={false} />
      ),
    },
    {
      headerName: 'S3 File',
      field: 's3FilePath',
      // Talabat stores multiple comma-separated file URLs (one per chunk) in
      // this field - each renders on its own line, and autoHeight grows the
      // row to fit them instead of clipping to the default row height.
      autoHeight: true,
      cellRenderer: (params: ICellRendererParams) => (
        <MultiLogFileCellRenderer params={params} />
      ),
    },
    {
      headerName: 'Creation / Completion Date',
      field: 'createdAt',
      autoHeight: true,
      cellRenderer: DateCellRenderer,
    },
    {
      headerName: 'Error Message',
      field: 'errorMessage',
      valueFormatter: (params: ValueFormatterParams) => params.value || '-',
    },
  ] as ColDef[]
